export type SocialNetwork = 'facebook' | 'instagram' | 'tiktok' | 'youtube' | 'twitter';

export interface SocialNormalizationResult {
  url: string;
  handle: string | null;
}

export class SocialNormalizationError extends Error {
  public readonly code: string;

  constructor(code: string, message?: string) {
    super(message ?? code);
    this.code = code;
    this.name = 'SocialNormalizationError';
  }
}

const HANDLE_PATTERN = /^[A-Za-z0-9._-]{2,100}$/;

const NETWORK_HOST: Record<SocialNetwork, string> = {
  facebook: 'facebook.com',
  instagram: 'instagram.com',
  tiktok: 'tiktok.com',
  youtube: 'youtube.com',
  twitter: 'x.com',
};

const CANONICAL_PREFIX: Record<SocialNetwork, string> = {
  facebook: 'https://www.facebook.com/',
  instagram: 'https://www.instagram.com/',
  tiktok: 'https://www.tiktok.com/@',
  youtube: 'https://www.youtube.com/',
  twitter: 'https://x.com/',
};

const NETWORK_SHORTHAND_ALIASES: Record<SocialNetwork, string[]> = {
  facebook: ['facebook'],
  instagram: ['instagram'],
  tiktok: ['tiktok'],
  youtube: ['youtube'],
  twitter: ['x', 'twitter'],
};

const trimSlashes = (value: string): string => value.replace(/^\/+|\/+$|\s+/g, '');

const sanitizeHandle = (raw: string): string => {
  const withoutAt = raw.startsWith('@') ? raw.slice(1) : raw;
  const cleaned = trimSlashes(withoutAt.split(/[?#]/)[0] ?? '');
  if (!cleaned) {
    throw new SocialNormalizationError('empty_handle', 'Falta el alias de la red social.');
  }
  if (!HANDLE_PATTERN.test(cleaned)) {
    throw new SocialNormalizationError('invalid_handle', 'Alias inválido.');
  }
  return cleaned;
};

const looksLikeUrl = (value: string): boolean =>
  /:\/\//.test(value) || value.startsWith('www.') || /^[a-z0-9.-]+\.[a-z]{2,}(?:\/|$)/i.test(value);

const ensureUrl = (candidate: string, network: SocialNetwork): URL => {
  const host = NETWORK_HOST[network];
  const value = looksLikeUrl(candidate)
    ? (/:\/\//.test(candidate) ? candidate : `https://${candidate.replace(/^\/+/, '')}`)
    : `https://${host.replace(/\/$/, '')}/${candidate.replace(/^\/+/, '')}`;
  const parsed = new URL(value, `https://${host}`);
  const allowedHosts = network === 'twitter' ? ['x.com', 'twitter.com'] : [host];
  if (!allowedHosts.some((allowed) => parsed.hostname === allowed || parsed.hostname.endsWith(`.${allowed}`))) {
    throw new SocialNormalizationError('invalid_domain', `La URL debe ser de ${host}.`);
  }
  return parsed;
};

const extractNetworkShorthand = (value: string, network: SocialNetwork): string | null => {
  const normalized = value.replace(/^\/+/, '');
  const lower = normalized.toLowerCase();
  const alias = NETWORK_SHORTHAND_ALIASES[network].find((entry) => lower.startsWith(`${entry}/`));
  if (!alias) {
    return null;
  }
  return normalized.slice(alias.length + 1);
};

const buildYoutube = (path: string): SocialNormalizationResult => {
  const clean = path.replace(/^\/+/, '').split(/[?#]/)[0] ?? '';
  if (!clean) {
    throw new SocialNormalizationError('empty_handle');
  }
  if (clean.startsWith('@')) {
    const handle = sanitizeHandle(clean);
    return { url: `${CANONICAL_PREFIX.youtube}${clean}`, handle };
  }
  for (const prefix of ['channel/', 'c/', 'user/']) {
    if (clean.toLowerCase().startsWith(prefix)) {
      const suffix = clean.slice(prefix.length).split('/')[0] ?? '';
      if (!suffix) {
        throw new SocialNormalizationError('empty_handle');
      }
      return { url: `${CANONICAL_PREFIX.youtube}${prefix}${suffix}`, handle: suffix };
    }
  }
  const handle = sanitizeHandle(clean);
  return { url: `${CANONICAL_PREFIX.youtube}@${handle}`, handle };
};

export const normalizeSocialInput = (
  network: SocialNetwork,
  raw: string,
): SocialNormalizationResult => {
  const value = raw.trim();
  if (!value) {
    throw new SocialNormalizationError('empty');
  }

  if (value.startsWith('@') || value.startsWith('/')) {
    const handle = sanitizeHandle(value);
    if (network === 'youtube') {
      return { url: `${CANONICAL_PREFIX.youtube}@${handle}`, handle };
    }
    if (network === 'tiktok') {
      return { url: `${CANONICAL_PREFIX.tiktok}${handle}`, handle };
    }
    return { url: `${CANONICAL_PREFIX[network]}${handle}`, handle };
  }

  const shorthand = extractNetworkShorthand(value, network);
  if (shorthand) {
    const handle = sanitizeHandle(shorthand);
    if (network === 'youtube') {
      return { url: `${CANONICAL_PREFIX.youtube}@${handle}`, handle };
    }
    if (network === 'tiktok') {
      return { url: `${CANONICAL_PREFIX.tiktok}${handle}`, handle };
    }
    return { url: `${CANONICAL_PREFIX[network]}${handle}`, handle };
  }

  const host = NETWORK_HOST[network];
  if (value.toLowerCase().includes(host)) {
    const parsed = ensureUrl(value, network);
    if (network === 'facebook' || network === 'instagram') {
      const handle = sanitizeHandle(parsed.pathname);
      return { url: `${CANONICAL_PREFIX[network]}${handle}`, handle };
    }
    if (network === 'tiktok') {
      const handle = sanitizeHandle(parsed.pathname.startsWith('@') ? parsed.pathname : parsed.pathname.replace(/^\/+/, ''));
      return { url: `${CANONICAL_PREFIX.tiktok}${handle}`, handle };
    }
    if (network === 'twitter') {
      const handle = sanitizeHandle(parsed.pathname);
      return { url: `${CANONICAL_PREFIX.twitter}${handle}`, handle };
    }
    return buildYoutube(parsed.pathname);
  }

  if (network === 'twitter' && value.toLowerCase().includes('twitter.com')) {
    const parsed = ensureUrl(value, network);
    const handle = sanitizeHandle(parsed.pathname);
    return { url: `${CANONICAL_PREFIX.twitter}${handle}`, handle };
  }

  const handle = sanitizeHandle(value);
  if (network === 'tiktok') {
    return { url: `${CANONICAL_PREFIX.tiktok}${handle}`, handle };
  }
  if (network === 'youtube') {
    return { url: `${CANONICAL_PREFIX.youtube}@${handle}`, handle };
  }
  return { url: `${CANONICAL_PREFIX[network]}${handle}`, handle };
};

export interface SocialWarningPayload {
  code: string;
  reason?: string | null;
}

export interface SettingsWarningsPayload {
  social?: Record<string, SocialWarningPayload>;
}

export const parseWarningsHeader = (raw: string | null | undefined): SettingsWarningsPayload | null => {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as SettingsWarningsPayload;
    if (parsed && typeof parsed === 'object') {
      return parsed;
    }
  } catch {
    return null;
  }
  return null;
};
